import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import type { CreateTimeEntryDto, Project } from 'api';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { ProjectPicker } from 'features/timer/components/ProjectPicker';
import { BillableChip } from 'features/timer/tracker.styles';
import React, { useEffect, useState } from 'react';
import { SearchableMultiSelect } from 'shared/components/SearchableMultiSelect';

import {
  addTimeEntryActionsSx,
  addTimeEntryCancelButtonSx,
  addTimeEntryDialogContentSx,
  addTimeEntryDialogTitleSx,
  addTimeEntryInputSx,
  addTimeEntryProjectRowSx,
  addTimeEntrySubmitButtonSx,
  addTimeEntryTimeFieldSx,
  addTimeEntryTimeRowSx,
} from '../reports.styles';

interface TeamMember {
  userId: string;
  name?: string;
  email: string;
  status?: string;
}

interface AddTimeEntryModalProps {
  open: boolean;
  onClose: () => void;
  /** Called once per selected user — each element is a ready-to-submit DTO */
  onSubmit: (entries: CreateTimeEntryDto[]) => void;
  teamMembers: TeamMember[];
  projects: Project[];
  currentUserId: string;
  canSelectUser: boolean;
  defaultDate?: string;
}

export const AddTimeEntryModal: React.FC<AddTimeEntryModalProps> = ({
  open,
  onClose,
  onSubmit,
  teamMembers,
  projects,
  currentUserId,
  canSelectUser,
  defaultDate,
}) => {
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [date, setDate] = useState<Dayjs>(defaultDate ? dayjs(defaultDate) : dayjs());
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [isBillable, setIsBillable] = useState(false);
  const [startTimeError, setStartTimeError] = useState('');
  const [endTimeError, setEndTimeError] = useState('');

  useEffect(() => {
    if (open) {
      setSelectedUserIds([]);
      setSelectedProjectId(null);
      setDescription('');
      setDate(defaultDate ? dayjs(defaultDate) : dayjs());
      setStartTime('09:00');
      setEndTime('10:00');
      setIsBillable(false);
      setStartTimeError('');
      setEndTimeError('');
    }
  }, [open, currentUserId, defaultDate]);

  const parseHHMM = (raw: string): { h: number; m: number } | null => {
    const match = raw.trim().match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return null;
    const h = parseInt(match[1], 10);
    const m = parseInt(match[2], 10);
    if (h > 23 || m > 59) return null;
    return { h, m };
  };

  const validate = (): boolean => {
    let valid = true;
    if (!parseHHMM(startTime)) {
      setStartTimeError('Enter a valid time (HH:MM)');
      valid = false;
    } else {
      setStartTimeError('');
    }
    if (!parseHHMM(endTime)) {
      setEndTimeError('Enter a valid time (HH:MM)');
      valid = false;
    } else {
      setEndTimeError('');
    }
    return valid;
  };

  const handleSubmit = () => {
    if (!selectedProjectId || !validate()) return;
    if (canSelectUser && selectedUserIds.length === 0) return;

    const dateStr = date.format('YYYY-MM-DD');
    const startDate = new Date(`${dateStr}T${startTime}:00`);
    const endDate = new Date(`${dateStr}T${endTime}:00`);
    if (endDate <= startDate) endDate.setDate(endDate.getDate() + 1);

    const base = {
      projectId: selectedProjectId,
      description: description.trim() || undefined,
      startedAt: startDate.toISOString(),
      endedAt: endDate.toISOString(),
      isBillable,
    };

    if (canSelectUser && selectedUserIds.length > 0) {
      onSubmit(selectedUserIds.map((userId) => ({ ...base, targetUserId: userId })));
    } else {
      onSubmit([base]);
    }
  };

  const userCount = canSelectUser ? selectedUserIds.length : 1;
  const isSubmittable =
    !!selectedProjectId &&
    !!startTime &&
    !!endTime &&
    (!canSelectUser || selectedUserIds.length > 0);

  const submitLabel = userCount > 1 ? `Add ${userCount} Entries` : 'Add Entry';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: '16px', p: 0 } } }}
    >
      <DialogTitle sx={addTimeEntryDialogTitleSx}>Add Time Entry</DialogTitle>

      <DialogContent sx={addTimeEntryDialogContentSx}>
        {/* User multi-select */}
        {canSelectUser && (
          <SearchableMultiSelect
            label="User"
            options={teamMembers.map((m) => ({
              id: m.userId,
              name: m.name || m.email,
              status: m.status,
            }))}
            value={selectedUserIds}
            onChange={setSelectedUserIds}
            statusField="status"
            bordered
          />
        )}

        {/* Description */}
        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          size="small"
          fullWidth
          multiline
          minRows={3}
          placeholder="What did you work on?"
          InputLabelProps={{ shrink: true }}
          sx={addTimeEntryInputSx}
        />

        {/* Project picker + Billable chip */}
        <Box sx={addTimeEntryProjectRowSx}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <ProjectPicker
              projects={projects}
              selectedId={selectedProjectId}
              onSelect={(p) => {
                setSelectedProjectId(p.id);
                setIsBillable(p.isBillable);
              }}
              formVariant
              popperZIndex={1500}
            />
          </Box>
          <Tooltip title={isBillable ? 'Mark as non-billable' : 'Mark as billable'}>
            <BillableChip
              active={isBillable ? 1 : 0}
              isClickable={1}
              onClick={() => setIsBillable((b) => !b)}
            >
              <AttachMoneyIcon sx={{ fontSize: 14 }} />
              {isBillable ? 'Billable' : 'Non-billable'}
            </BillableChip>
          </Tooltip>
        </Box>

        {/* Date */}
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Date"
            value={date}
            onChange={(v) => v && setDate(v)}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                InputLabelProps: { shrink: true },
                sx: addTimeEntryInputSx,
              },
            }}
          />
        </LocalizationProvider>

        {/* Start / End time */}
        <Box sx={addTimeEntryTimeRowSx}>
          <TextField
            label="Start time"
            value={startTime}
            onChange={(e) => {
              setStartTime(e.target.value);
              setStartTimeError('');
            }}
            size="small"
            placeholder="HH:MM"
            error={!!startTimeError}
            helperText={startTimeError}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={addTimeEntryTimeFieldSx}
          />
          <TextField
            label="End time"
            value={endTime}
            onChange={(e) => {
              setEndTime(e.target.value);
              setEndTimeError('');
            }}
            size="small"
            placeholder="HH:MM"
            error={!!endTimeError}
            helperText={endTimeError}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={addTimeEntryTimeFieldSx}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={addTimeEntryActionsSx}>
        <Button onClick={onClose} sx={addTimeEntryCancelButtonSx}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isSubmittable}
          sx={addTimeEntrySubmitButtonSx}
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
